import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import SessionExpiredNotice from '~/components/auth/SessionExpiredNotice';
import JobsWhatsappBubble from '~/components/shell/JobsWhatsappBubble';
import AuthGuard from '~/components/auth/AuthGuard';
import { Waiting } from '~/components/ui';
import {
  JobApplyConnectPage, JobApplyCVPage,
  JobApplyDashboardPage, JobApplyPreferencesPage, JobApplyHistoryPage,
  JobApplyInvitationsPage, JobApplyLayout, JobApplyOnboardingPage,
  JobApplyMessagesPage, JobApplyThreadPage,
  JobApplyProfileLayout, JobApplyTalentPoolPage, JobApplyPlanPage,
  JobApplyScreeningPage, JobApplyPartnerPage, JobApplyEnglishPage,
  JobApplyImproveCVPage,
} from '~/screens/job-apply';

// The operator console is a different audience from the applicant flow: split it
// out so an applicant never downloads it.
const AdminLayout = lazy(() => import('~/screens/job-apply/admin/AdminLayout'));
const AdminOverviewPage = lazy(() => import('~/screens/job-apply/admin/AdminOverviewPage'));
const AdminJobsPage = lazy(() => import('~/screens/job-apply/admin/AdminJobsPage'));
const AdminUsersPage = lazy(() => import('~/screens/job-apply/admin/AdminUsersPage'));
const AdminWorkersPage = lazy(() => import('~/screens/job-apply/admin/AdminWorkersPage'));
const AdminAgenciesPage = lazy(() => import('~/screens/job-apply/admin/AdminAgenciesPage'));
const AdminPartnersPage = lazy(() => import('~/screens/job-apply/admin/AdminPartnersPage'));
const AdminEnglishPage = lazy(() => import('~/screens/job-apply/admin/AdminEnglishPage'));

// The agency console is a third audience — recruiters, not applicants — and most
// people who sign in here will never open it. Same reasoning as the admin split.
const AgencyLayout = lazy(() => import('~/screens/agency/AgencyLayout'));
const AgencyLandingPage = lazy(() => import('~/screens/agency/AgencyLandingPage'));
// La landing del socio comercial: pública, lazy por la misma razón que la de
// agencias (casi nadie de los que entran a la app la va a abrir).
const AgencyOverviewPage = lazy(() => import('~/screens/agency/AgencyOverviewPage'));
const AgencyPositionsPage = lazy(() => import('~/screens/agency/AgencyPositionsPage'));
const AgencyCandidatesPage = lazy(() => import('~/screens/agency/AgencyCandidatesPage'));
const AgencyTeamPage = lazy(() => import('~/screens/agency/AgencyTeamPage'));
const AgencyCreditsPage = lazy(() => import('~/screens/agency/AgencyCreditsPage'));
const AgencyPositionFormPage = lazy(() => import('~/screens/agency/AgencyPositionFormPage'));
const AgencyPositionDetailPage = lazy(() => import('~/screens/agency/AgencyPositionDetailPage'));

const LoginPage = lazy(() => import('~/screens/auth/LoginPage'));
const CallbackPage = lazy(() => import('~/screens/auth/CallbackPage'));
// El enlace mágico del correo cuando el login empezó acá (ContinuePage).
const ContinuePage = lazy(() => import('~/screens/auth/ContinuePage'));

function LazyFallback() {
  return <Waiting />;
}

/** Un lazy envuelto. El `<Suspense>` por ruta se repetía quince veces. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>;
}

/**
 * This client IS job-apply, so the screens hang off `/` — in web-exapps the same
 * screens live under a `/job-apply` prefix because they share that SPA with a
 * dozen unrelated apps. The API paths keep the prefix: it is the mount point on
 * conversations-service, not a UI route.
 *
 * ── TRES SUPERFICIES, TRES SHELLS ───────────────────────────────────────────
 *
 * `/`        el candidato. Panel, Mensajes, Postulaciones, Perfil.
 * `/agency`  la agencia. Riel propio; sólo entra quien tiene asiento.
 * `/admin`   la operación. Riel propio; sólo entra un operador.
 * `/agencies`, `/agencias` y `/empresas`: landing pública para agencias y empleadores.
 * La landing del Programa de socios ya no vive aquí: está en
 * tbit.app/es/job-apply/socios y nginx redirige con 301 `/programa-de-socios` y
 * `/partner-program`. Su CTA lleva a `/socios` (con sesión).
 *
 * Cada una monta su propio layout y su propio nav. Antes las tres compartían la
 * barra del candidato: el operador veía "Workers" al lado de "Hoja de vida", y
 * cualquiera podía escribir /admin/overview a mano porque el `isOperator` sólo
 * decidía si se DIBUJABA el enlace. Ahora el permiso decide si la ruta existe.
 *
 * ── REDIRECCIONES ───────────────────────────────────────────────────────────
 *
 * Cada ruta que se movió deja un `<Navigate replace>` en su vieja dirección. No
 * es cortesía: estos enlaces están pegados en correos ya enviados y en chats de
 * soporte, y ninguno de esos se puede reescribir. `replace` para que el botón
 * "atrás" no rebote entre la vieja y la nueva.
 */
export function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <SessionExpiredNotice />
      {/* La burbuja de WhatsApp de TBit Jobs: en las landings públicas y en la
          app del candidato; no en las consolas ni donde taparía un botón. */}
      <JobsWhatsappBubble />
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<Lazy><LoginPage /></Lazy>} />
        <Route path="/agencias" element={<Lazy><AgencyLandingPage /></Lazy>} />
        <Route path="/agencies" element={<Lazy><AgencyLandingPage /></Lazy>} />
        <Route path="/empresas" element={<Lazy><AgencyLandingPage /></Lazy>} />
        <Route path="/auth/callback" element={<Lazy><CallbackPage /></Lazy>} />
        <Route path="/auth/continue" element={<Lazy><ContinuePage /></Lazy>} />

        {/* Job Apply (OIDC required) */}
        <Route element={<AuthGuard />}>
          {/* ── Candidato ─────────────────────────────────────────────────── */}
          <Route path="/" element={<JobApplyLayout />}>
            <Route index element={<JobApplyDashboardPage />} />
            <Route path="onboarding" element={<JobApplyOnboardingPage />} />
            {/* La segunda puerta de la hoja de vida: el cuestionario que la
                escribe para quien no tiene un PDF a mano. Hermana de
                `onboarding` y no una ruta suelta, porque es el mismo alta — y
                por eso mismo el shell la deja abierta con la cuenta a medias.
                En el bundle del candidato, no en un lazy: se llega desde el
                paso anterior, y un spinner en medio del alta es una salida. */}
            <Route path="onboarding/screening" element={<JobApplyScreeningPage />} />
            <Route path="connect" element={<JobApplyConnectPage />} />
            <Route path="dashboard" element={<JobApplyDashboardPage />} />
            {/* `/panel` es a donde Stripe devuelve al candidato después del
                checkout (`success_url`/`cancel_url` del backend). Es el panel,
                con su `?checkout=` intacto: sin esta ruta el regreso del pago
                caía en la pantalla de "no encontrada". */}
            <Route path="panel" element={<JobApplyDashboardPage />} />

            {/* El inbox: la lista y el hilo. Un hilo ES una application, y por
                eso el parámetro se llama applicationId y no threadId. */}
            <Route path="mensajes" element={<JobApplyMessagesPage />} />
            <Route path="mensajes/:applicationId" element={<JobApplyThreadPage />} />

            <Route path="postulaciones" element={<JobApplyHistoryPage />} />

            {/* Perfil: tres tabs, tres rutas. Que sean rutas y no estado local
                es lo que permite enlazar "sube tu hoja de vida" desde un correo. */}
            <Route path="perfil" element={<JobApplyProfileLayout />}>
              <Route index element={<Navigate to="/perfil/hoja-de-vida" replace />} />
              <Route path="hoja-de-vida" element={<JobApplyCVPage />} />
              {/* «Agregar estos datos»: una tarjeta por carencia y Mis datos. */}
              <Route path="hoja-de-vida/mejorar" element={<JobApplyImproveCVPage />} />
              <Route path="preferencias" element={<JobApplyPreferencesPage />} />
              <Route path="bolsa-de-talento" element={<JobApplyTalentPoolPage />} />
              <Route path="plan" element={<JobApplyPlanPage />} />
              <Route path="socios" element={<JobApplyPartnerPage />} />
              {/* A donde llevan los correos de la evaluación de inglés. */}
              <Route path="ingles" element={<JobApplyEnglishPage />} />
            </Route>

            {/* El Programa de socios, suelto: la dirección que se comparte, la
                del correo de aprobación y a la que vuelve Stripe al configurar
                los cobros. Abre aunque el alta de candidato esté a medias (ver
                `useOnboardingGate`): un socio puede no postular a nada. La misma
                pantalla vive como tab en /perfil/socios para quien ya es
                candidato. NO es /agency: eso es para empresas que publican. */}
            <Route path="socios" element={<JobApplyPartnerPage standalone />} />

            {/* Both paths are the same screen: the second is the link in the
                invitation email, which focuses one row of the inbox. Sigue viva
                y sin badge propio: `pending_invitations` ya está contado en el
                de Mensajes, y ponerlo en dos lugares lo cuenta dos veces. */}
            <Route path="invitations" element={<JobApplyInvitationsPage />} />
            <Route path="invitations/:matchId" element={<JobApplyInvitationsPage />} />

            {/* Las direcciones viejas del candidato. */}
            <Route path="cv" element={<Navigate to="/perfil/hoja-de-vida" replace />} />
            <Route path="preferences" element={<Navigate to="/perfil/preferencias" replace />} />
            <Route path="history" element={<Navigate to="/postulaciones" replace />} />
          </Route>

          {/* ── Operación ─────────────────────────────────────────────────── */}
          <Route path="/admin" element={<Lazy><AdminLayout /></Lazy>}>
            <Route index element={<Lazy><AdminOverviewPage /></Lazy>} />
            <Route path="users" element={<Lazy><AdminUsersPage /></Lazy>} />
            <Route path="jobs" element={<Lazy><AdminJobsPage /></Lazy>} />
            <Route path="agencies" element={<Lazy><AdminAgenciesPage /></Lazy>} />
            {/* La bandeja del Programa de socios; el correo a David enlaza acá. */}
            <Route path="socios" element={<Lazy><AdminPartnersPage /></Lazy>} />
            <Route path="workers" element={<Lazy><AdminWorkersPage /></Lazy>} />
            <Route path="ingles" element={<Lazy><AdminEnglishPage /></Lazy>} />
            {/* /admin/overview era la portada cuando estas rutas colgaban del
                shell del candidato. */}
            <Route path="overview" element={<Navigate to="/admin" replace />} />
          </Route>

          {/* ── Agencia ───────────────────────────────────────────────────────
              Su propio layout, que es también el portero del asiento: AuthGuard
              prueba que hay sesión, AgencyLayout prueba que la sesión pertenece
              a alguien con un puesto en una agencia. */}
          <Route path="/agency" element={<Lazy><AgencyLayout /></Lazy>}>
            <Route index element={<Lazy><AgencyOverviewPage /></Lazy>} />
            <Route path="positions" element={<Lazy><AgencyPositionsPage /></Lazy>} />
            <Route path="positions/new" element={<Lazy><AgencyPositionFormPage /></Lazy>} />
            <Route path="positions/:id" element={<Lazy><AgencyPositionDetailPage /></Lazy>} />
            <Route path="positions/:id/edit" element={<Lazy><AgencyPositionFormPage /></Lazy>} />
            <Route path="candidates" element={<Lazy><AgencyCandidatesPage /></Lazy>} />
            <Route path="team" element={<Lazy><AgencyTeamPage /></Lazy>} />
            <Route path="evaluaciones" element={<Lazy><AgencyCreditsPage /></Lazy>} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
